import "./ClientPageContainer.scss";
import "./ClientForm.scss";

import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import { ControlledTextArea } from "components/inputs/TextArea/ControlledTextArea";
import { useCallback, useEffect, useMemo } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  setFocusedField,
  setIsLoading,
  setShowUploadTemplateModal,
} from "reducers/main";
import { useLazyGetAdjusterImportTemplateQuery } from "services/api";
import {
  useGetClientByIdQuery,
  usePatchClientByIdMutation,
} from "services/client-api";
import type { ClientType } from "types/DataTypes";
import { useDispatch, useSelector } from "utils/redux";

import ClientFeedsDrawer from "./ClientFeedsDrawer/ClientFeedsDrawer";
import ClientExportFeedsDrawer from "./ClientFeedsDrawer/ClientExportFeedsDrawer";
import ClientDocumentsDrawer from "./ClientDocumentsDrawer/ClientDocumentsDrawer";

import ClientFilesDrawer from "./ClientFilesDrawer/ClientFilesDrawer";
import ClientPageContainer from "./ClientPageContainer";
import { AdjusterTable } from "./components/AdjusterTable";
import ClientInformation from "./components/ClientInformation";
import { CustomServicesForm } from "./components/CustomServicesForm";
import UploadAdjusterTemplateModal from "./components/UploadAdjusterTemplateModal";
import GeneralClientSettings from "./GeneralClientSettings";
import SpecialClientInstructions from "./SpecialClientInstructions";

const CommunicationInstructions = ({
  client,
  onSave,
}: {
  client: ClientType;
  onSave: (field: string, value: unknown) => Promise<void>;
}) => {
  const {
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      communications_instructions: client?.communications_instructions || "",
    },
  });

  useEffect(() => {
    if (client?.communications_instructions !== undefined) {
      reset(
        {
          communications_instructions: client.communications_instructions,
        },
        {
          keepDirtyValues: true,
        },
      );
    }
  }, [client?.communications_instructions, reset]);

  return (
    <Card heading="Communication Instructions" type="full-width">
      <ControlledTextArea
        name="communications_instructions"
        control={control}
        advancedEdit={true}
        onSave={(value) => onSave("communications_instructions", value)}
        errors={errors}
      />
    </Card>
  );
};

const ClientForm = () => {
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId?: string }>();

  const constants = useSelector((state) => state.shared.constants);
  const downloadTemplate = useSelector((state) => state.main.downloadTemplate);
  const showUploadTemplateModal = useSelector(
    (state) => state.main.showUploadTemplateModal,
  );

  const { data: client, isLoading } = useGetClientByIdQuery(
    { id: clientId },
    { skip: !clientId },
  );

  const [patchClientById] = usePatchClientByIdMutation();
  const [triggerGetImportTemplate] = useLazyGetAdjusterImportTemplateQuery();
  const { reset, clearErrors, setError } = useFormContext();

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (client && !isLoading) {
      reset(client, { keepDirtyValues: true });
    }
  }, [client, reset, isLoading]);

  useEffect(() => {
    if (downloadTemplate) {
      handleDownloadTemplate();
    }
  }, [downloadTemplate]);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      if (!clientId) return;

      try {
        await patchClientById({
          id: clientId,
          data: { [field]: value },
        }).unwrap();
        clearErrors(field);
        dispatch(setFocusedField(null));
      } catch (error) {
        // error.data will contain field-specific validation errors
        const message =
          error.data?.[field] || "An error occurred while updating the client";
        setError(field, {
          type: error.status || "error",
          message: Array.isArray(message) ? message[0] : message,
        });
        KaboodleToaster.error(Array.isArray(message) ? message[0] : message);
        // set focused field to show red border
        dispatch(setFocusedField(field));
      }
    },
    [patchClientById, clientId, setError, clearErrors],
  );

  const handleDownloadTemplate = useCallback(async () => {
    await triggerGetImportTemplate({
      fileName: "adjuster_import_template.csv",
      rreId: null,
      url: "query",
    })
      .unwrap()
      .then(() => reset());
  }, [triggerGetImportTemplate, reset]);

  const handleCloseModal = useCallback(() => {
    dispatch(setShowUploadTemplateModal(false));
  }, [dispatch]);

  const modal = useMemo(
    () => (
      <UploadAdjusterTemplateModal
        isOpen={showUploadTemplateModal}
        onClose={handleCloseModal}
      />
    ),
    [showUploadTemplateModal, handleCloseModal],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
    }
  };

  return (
    <>
      {modal}
      <ClientPageContainer>
        <form className="overview" onKeyDown={handleKeyDown} autoComplete="off">
          <ClientInformation onSave={handleSave} />
          <CommunicationInstructions client={client} onSave={handleSave} />
          <GeneralClientSettings client={client} onSave={handleSave} />
          <CustomServicesForm onSave={handleSave} constants={constants} />
          <SpecialClientInstructions client={client} />
          <AdjusterTable />
        </form>
      </ClientPageContainer>
      <ClientFilesDrawer />
      <ClientDocumentsDrawer />
      <ClientFeedsDrawer />
      <ClientExportFeedsDrawer />
    </>
  );
};

export default ClientForm;
